import { useEffect, useState } from "react";
import { storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import Header from "./Header";
import Footer from "./Footer";
import {
  HeroImageWithSkeleton,
  GalleryImageWithSkeleton,
} from "./ImageSkeleton";
import { Helmet } from "react-helmet-async";

const MainPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const galleryImages = [
    "2023-45.webp",
    "2022-44.webp",
    "2020-43.webp",
    "2017-38.webp",
  ];

  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [galleryImageUrls, setGalleryImageUrls] = useState<string[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const loadImages = async () => {
      try {
        // Load hero image
        const heroRef = ref(storage, "artwork/2018-41.webp");
        const heroUrl = await getDownloadURL(heroRef);
        setHeroImageUrl(heroUrl);

        // Load gallery images
        const galleryUrls = await Promise.all(
          galleryImages.map(async (imageName) => {
            const galleryRef = ref(storage, `artwork/${imageName}`);
            const url = await getDownloadURL(galleryRef);
            return url;
          })
        );
        setGalleryImageUrls(galleryUrls);
        console.log("All images loaded successfully");
      } catch (error) {
        console.error("Error loading images:", error);
        if (error instanceof Error) {
          console.error("Error details:", {
            message: error.message,
            name: error.name,
            stack: error.stack,
          });
        }
      }
    };

    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>이선희 작가 | Lee Sun Hee Artist</title>
        <meta
          name="description"
          content="이선희 작가의 작품 세계와 작품 활동을 소개하는 공식 웹사이트입니다."
        />
      </Helmet>
      <div className="relative min-h-screen bg-white">
        <Header />
        <main className="w-full">
          {/* Hero Section */}
          <section className="relative h-screen w-full overflow-hidden">
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `translateY(${scrollY * 0.5}px)`,
              }}
            >
              <HeroImageWithSkeleton
                imageUrl={heroImageUrl || "/api/placeholder/1920/1080"}
                alt="Main artwork"
                className="w-5/6 h-4/5 object-cover"
              />
            </div>
            {/* Artist Name */}
            <div
              className="absolute bottom-1 left-0 right-0 bg-transparent"
              style={{
                transform: `translateY(${Math.min(scrollY * 0.3, 100)}px)`,
                opacity: 1 - scrollY / 500,
              }}
            >
              <div className="mx-auto px-6 pb-12 text-center">
                <h1
                  className="leading-none text-gray-900 md:hidden pb-4"
                  style={{
                    fontFamily: '"forma-djr-display", sans-serif',
                    fontWeight: 800,
                    fontSize: "clamp(2rem, 10vw, 6rem)",
                    fontStyle: "normal",
                  }}
                >
                  LEE SUN HEE
                </h1>
                <h1
                  className="leading-none text-gray-900 hidden md:block"
                  style={{
                    fontFamily: '"forma-djr-display", sans-serif',
                    fontWeight: 800,
                    fontSize: "clamp(4rem, 15vw, 12rem)",
                    fontStyle: "normal",
                  }}
                >
                  LEE SUN HEE
                </h1>
              </div>
            </div>
          </section>
          {/* 수정된 Gallery Section */}
          <section className="relative bg-white py-24">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-[1240px] mx-auto">
                {galleryImageUrls.map((imageUrl, index) => (
                  <div
                    key={index}
                    className="w-full h-full overflow-hidden mx-auto"
                    style={{
                      transform: `translateY(${Math.max(
                        0,
                        scrollY * 0.1 - 200
                      )}px)`,
                      opacity: Math.min(1, (scrollY - 300) / 500),
                    }}
                  >
                    <GalleryImageWithSkeleton
                      imageUrl={imageUrl || `/api/placeholder/590/740`}
                      alt={`Gallery item ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer></Footer>
      </div>
    </>
  );
};

export default MainPage;
